// @ts-strict-ignore
import React, { useRef, useState } from 'react'

import { useSelector } from 'react-redux'

import { AppMetaData, PlayerStatus, AudioPlayerContainer as Container, PlaylistItem } from 'mytours-read-common'
import { BetaFeatures } from 'mytours-read-api-typescript'

import { getDirMode, isRTL, waitForSomething } from '../../platform'

import { formatPlayPauseLabel } from '../functions'
import { useAudioPlayerSpace } from '../useAudioPlayerSpace'
import { getCurrentSound, onBack, onForward, sendAnalyticsEvent, DEFAULT_SKIP_SECONDS } from '../soundController'
import { EventActionsEnum } from '../../logging/analytics'

import FullscreenAudioPlayer from './FullscreenAudioPlayer'
import NewAudioPlayerBar from './v2/AudioPlayerBar'
import { Button, ProgressBar, Text, Grid, GridRow, GridCol } from '@mytours/components'

import { containerClose, containerDisappear, containerOpen } from './animations'

import strings from '../../../strings'

import classNames from 'classnames'
import './styles/style.less'
import { useEffect } from 'react'
import usePrevious from 'common_lib/hooks/use-previous'
import { useMemo } from 'react'
import { deleteSectionsQueryParams, getSectionsQueryParams } from 'mobile_web/modules/navigation/queryParams'

interface Props {
	className?: string
	bottomOffset?: number
}

// The mobile_app_api response carries beta_features (mytours-web 535566571dc014) but the
// regenerated SDK at 30e1b7a only typed BetaFeatures on ProjectApp / ProjectGuide, not on
// GetAppShowResponse / AppMetaData. Cast just the outer dereference; the inner shape is typed.
const selectNewAudioPlayerEnabled = (state: { settings?: { appMetaData?: AppMetaData } }): boolean => {
	const betaFeatures = (state.settings?.appMetaData as { beta_features?: BetaFeatures } | undefined)?.beta_features
	return betaFeatures?.new_audio_player === true
}

const AudioPlayer: React.FC<Container.Props & Container.Actions & Props> = props => {
	const newAudioPlayerEnabled = useSelector(selectNewAudioPlayerEnabled)
	if (newAudioPlayerEnabled) {
		return <NewAudioPlayerBar bottomOffset={props.bottomOffset} className={props.className} />
	}

	const [showFullScreenPlayer, setShowFullScreenPlayer] = useState<boolean>(false)
	const [animationProcess, setAnimationProcess] = useState<boolean>(false)

	const audioWrapperRef = useRef<HTMLDivElement>()
	const audioWrapperRefNode = audioWrapperRef?.current

	const audioRef = useRef<HTMLDivElement>()
	const audioRefNode = audioRef?.current

	const prevCurrentAudio: PlaylistItem = usePrevious(props.currentAudio)

	const bottomOffset = useMemo<number>(() => (props.bottomOffset > -1 ? props.bottomOffset : 56), [props.bottomOffset])

	const showAudioPlayer = useMemo<boolean>(() => {
		const { playerStatus } = props.currentAudio || {}
		if (playerStatus !== PlayerStatus.Pause && playerStatus !== PlayerStatus.Playing) {
			return false
		}

		return true
	}, [props.currentAudio?.playerStatus])

	const isPlaying = useMemo<boolean>(
		() => props.currentAudio?.playerStatus === PlayerStatus.Playing,
		[props.currentAudio?.playerStatus],
	)

	const wrapperStyles =
		showFullScreenPlayer || animationProcess
			? {
					paddingBottom: bottomOffset + 14,
				}
			: {}

	const toggleFullScreenPlayerModal = (): void => {
		setAnimationProcess(true)
		setShowFullScreenPlayer(!showFullScreenPlayer)
	}

	const onGoFullscreenPlayer = (): void => {
		const { currentAudio, collection } = props

		if (!showFullScreenPlayer) {
			sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioOpenPlayer)
		}

		toggleFullScreenPlayerModal()
	}

	const openFullScreenAudio = (): void => {
		const { audioTrack } = props.currentAudio || {}

		if (audioTrack?.coverImages?.length > 0 && (audioTrack.slideshowEnabled || audioTrack.fullScreenEnabled)) {
			onGoFullscreenPlayer()
		}
	}

	useEffect(() => {
		if (props?.currentAudio?.playerStatus === PlayerStatus.Ended) {
			setShowFullScreenPlayer(false)
		}
	}, [props.currentAudio])

	useEffect(
		() => () => {
			setShowFullScreenPlayer(false)
		},
		[],
	)

	useEffect(() => {
		if (prevCurrentAudio?.audioTrack?.audio?.id === props.currentAudio?.audioTrack?.audio?.id) return // audio should be loaded only once

		const { storySectionId, sectionItemId } = getSectionsQueryParams()

		if (!storySectionId || !sectionItemId) return

		const { currentAudio } = props
		const { audioTrack, container } = currentAudio
		const { file, id: audioId } = audioTrack?.audio || {}
		const { id: fileId } = file

		if (sectionItemId !== audioId || storySectionId !== container?.id) return

		waitForSomething(() => getCurrentSound(audioId, container, fileId)).then(() => {
			onGoFullscreenPlayer()
			deleteSectionsQueryParams()
		})
	}, [props.currentAudio, props.container])

	useAudioPlayerSpace(showAudioPlayer)

	useEffect(() => {
		if (showFullScreenPlayer) {
			containerOpen(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		} else {
			containerClose(audioRefNode, bottomOffset)
				.onFinish(() => {
					setAnimationProcess(false)
				})
				.play()
		}
	}, [showFullScreenPlayer])

	useEffect(() => {
		const { playerStatus } = props.currentAudio || {}

		const isActive =
			prevCurrentAudio?.playerStatus !== playerStatus &&
			playerStatus === PlayerStatus.Playing &&
			prevCurrentAudio?.playerStatus !== PlayerStatus.Pause

		if (isActive) {
			openFullScreenAudio()
		}
	}, [props.currentAudio])

	const onPlayPause = (): void => {
		const { playerStatus, audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}
		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		if (currentSound && audio) {
			switch (playerStatus) {
				case PlayerStatus.Playing:
					currentSound.pause()
					return
				case PlayerStatus.Pause:
					currentSound.play()
					return
			}
		}
	}

	const onClickBack = (): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipBackward)
		onBack(audio?.id, container, audio?.file?.id, DEFAULT_SKIP_SECONDS, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onClickForward = (): void => {
		const { setCurrentTime, currentAudio, collection } = props

		const { duration, audioTrack, container } = currentAudio || {}
		const { audio } = audioTrack || {}

		sendAnalyticsEvent(currentAudio, collection, EventActionsEnum.AudioSkipForward)
		onForward(audio?.id, duration, container, audio?.file?.id, DEFAULT_SKIP_SECONDS, time => {
			setCurrentTime(audio?.id, time, container, audio?.file?.id)
		})
	}

	const onStopSound = (): void => {
		const { audioTrack, container } = props.currentAudio || {}
		const { audio } = audioTrack || {}

		const currentSound = getCurrentSound(audio?.id, container, audio?.file?.id)

		containerDisappear(audioWrapperRefNode)
			.onFinish(() => {
				if (currentSound) {
					currentSound.stop()
				}

				setShowFullScreenPlayer(false)
			})
			.play()
	}

	const renderFullScreenAudioPlayer = (): JSX.Element => {
		const { currentAudio, collection } = props

		return (
			<FullscreenAudioPlayer
				collection={collection}
				currentAudio={currentAudio}
				onPlayPause={onPlayPause}
				onBack={onClickBack}
				onForward={onClickForward}
			/>
		)
	}

	const { currentTime, duration, audioTrack } = props.currentAudio || {}
	const title = audioTrack?.audio?.title || ''

	const renderAudioBarTop = (): JSX.Element => {
		const progressPercent: number | undefined = currentTime / duration

		return (
			<>
				<GridRow>
					<ProgressBar
						variant="determinate"
						dir={getDirMode()}
						className="media-progress-bar"
						value={progressPercent}
						aria-label={strings.accessibility_audio_seek}
					/>
				</GridRow>
				<GridRow className="title">
					<GridCol>
						<Text variant="inherit" style={{ minHeight: '1rem' }}>
							{title}
						</Text>
					</GridCol>
				</GridRow>
			</>
		)
	}

	const renderAudioBarControls = (isPlaying: boolean): JSX.Element => (
		<GridRow className={`controls ${isRTL() ? 'rtl' : ''}`}>
			<GridCol>
				<Button
					variant="clear"
					className="time-controls-btn"
					onClick={onClickBack}
					aria-label={
						strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
					}
				>
					<i
						className="fak fa-skip-back-10"
						aria-label={
							strings.formatString(strings.accessibility_seek_backward, { seconds: DEFAULT_SKIP_SECONDS }) as string
						}
					/>
				</Button>
			</GridCol>
			<GridCol>
				<Button
					variant="clear"
					onClick={onPlayPause}
					className={classNames(isPlaying ? 'pause-controls-btn' : 'play-controls-btn')}
					aria-label={formatPlayPauseLabel(isPlaying, title)}
					// `ariaLabel` won't change unless we remount
					key={formatPlayPauseLabel(isPlaying, title)}
				>
					<i className={`fas fa-${isPlaying ? 'pause' : 'play'}`} />
				</Button>
			</GridCol>
			<GridCol>
				<Button
					variant="clear"
					className="time-controls-btn"
					onClick={onClickForward}
					aria-label={
						strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
					}
				>
					<i
						className="fak fa-skip-forward-10"
						aria-label={
							strings.formatString(strings.accessibility_seek_forward, { seconds: DEFAULT_SKIP_SECONDS }) as string
						}
					/>
				</Button>
			</GridCol>
		</GridRow>
	)

	if (!showAudioPlayer) {
		return null
	}

	return (
		<div className="audio-player-wrapper" ref={audioWrapperRef} dir={getDirMode()}>
			<div className={classNames('audio-player-inner', { fixed: animationProcess || showFullScreenPlayer })}>
				<Grid
					className={classNames(props.className, 'audio-player audio-player-bar', { open: showFullScreenPlayer })}
					style={wrapperStyles}
					ref={audioRef}
				>
					<GridRow className="audio-player-row">
						{!showFullScreenPlayer && renderAudioBarTop()}
						<GridRow className="actions">
							<GridCol>
								<Button
									variant="clear"
									onClick={onGoFullscreenPlayer}
									aria-label={strings.accessibility_open_fullscreen_player}
								>
									<i className={`far fa-${showFullScreenPlayer ? 'chevron-down' : 'chevron-up'}`} />
								</Button>
							</GridCol>
							{!showFullScreenPlayer && renderAudioBarControls(isPlaying)}
							<GridCol>
								<Button variant="clear" onClick={onStopSound} aria-label={strings.accessibility_finish_audio_track}>
									<i className="fal fa-xmark" />
								</Button>
							</GridCol>
						</GridRow>

						{showFullScreenPlayer && !animationProcess && renderFullScreenAudioPlayer()}
					</GridRow>
				</Grid>
			</div>
		</div>
	)
}

const AudioPlayerBarComponent = Container.default(AudioPlayer)
export default AudioPlayerBarComponent as any
